import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import { withFormik } from 'formik';
import { withTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import * as yup from 'yup';

import { Container, Row, Column } from 'common/components/styleguide/grid';
import InputGroup from 'common/components/styleguide/form/InputGroup';

import { capfl, formatPrice } from 'common/utils';
import { withCssUnit } from 'common/components/styleguide/utils';
import Button from 'common/components/styleguide/Button';
import Icon from 'common/components/styleguide/Icon';
import Link from 'common/components/styleguide/Link';
import Image from 'common/components/_base/Image';
import StaticImage from 'common/components/styleguide/Image';
import SelectGroup from 'common/components/styleguide/form/SelectCustomGroup';
import Price from 'common/components/_base/Price';
import Modal from 'common/components/_partials/modal/mobilonline/index';

import { getUserSelector } from 'common/selectors/user';
import {
  getPageSelector,
  getPageIsLoadingSelector
} from 'common/selectors/page';
import { getWebInstanceSettingsSelector } from 'common/selectors/index';
import { createReservation } from 'common/actions/ReservationActions';

import SS from 'common/pages/_style/';

import { fireGTMEvent } from 'common/utils/gtm';
import { PRODUCT_RESERVATION } from 'common/constants/GTMConstants';

import loader from 'assets/images/loader.svg';
import iconSmile from 'assets/images/mobilonline/icon_smile.png';
import store1 from 'assets/images/mobilonline/store_1.png';
import store2 from 'assets/images/mobilonline/store_2.png';

const ERROR_NAME_NOTHING_PICKED = 'nothing_picked';

const S = {};
S.SuccessWrapper = styled.div`
  align-self: center;
  justify-content: center;
  text-align: center;
`;

S.SuccessTitle = styled.h1`
  font-size: ${({ theme }) =>
    _.get(theme, 'reservation.success.heading.fontSize', '40px')};
  color: ${({ theme }) => _.get(theme, 'colors.primary', '#000')};
  white-space: pre-line;
  width: 100%;
  margin: 0 0 50px 0;
`;

S.StaticHeadingImage = styled(StaticImage)`
  max-width: ${({ theme }) =>
    _.get(theme, 'reservation.success.heading.imgMaxWidth', '40px')};
  display: inline;
  vertical-align: text-top;
  margin-left: 10px;
`;

S.Column = styled(Column)`
  text-align: center;
  color: ${({ theme }) => _.get(theme, 'colors.primary', '#000')};
  font-size: 1em;
  align-items: center;

  ${({ hasSeparator }) =>
    hasSeparator &&
    css`
      border-right: none;
      @media (min-width: ${({ theme }) => withCssUnit(theme.breakpoints.sm)}) {
        border-right: ${({ theme }) =>
          _.get(theme, 'reservation.success.visitUs.borderRight', 'none')};
      }
    `};

  @media (min-width: ${({ theme }) => withCssUnit(theme.breakpoints.sm)}) {
    text-align: left;
    font-size: 0.9em;
    &:first-child {
      text-align: right;
    }
  }
`;

S.Span = styled.span`
  color: ${({ theme }) =>
    _.get(
      theme,
      'reservation.success.visitUs.color',
      _.get(theme, 'colors.secondary', '#000')
    )};
  font-size: ${({ theme }) =>
    _.get(theme, 'reservation.success.visitUs.fontSize', '25px')};
  font-weight: bold;
  margin-bottom: 10px;
  display: inline-block;
  margin-top: 4px;

  @media (min-width: ${({ theme }) => withCssUnit(theme.breakpoints.sm)}) {
    margin-bottom: 0;
  }
`;

S.StoreImage = styled(StaticImage)`
  width: 80%;
  margin-top: 30px;
  max-width: 300px;
`;

const SuccessfullySent = ({ t }) => {
  return (
    <S.SuccessWrapper>
      <Row>
        <Column size={{ xs: 12 }}>
          <S.SuccessTitle>
            {capfl(t('thanks\n for your reservation'))}
            <S.StaticHeadingImage src={iconSmile} staticImage={true} alt="" />
          </S.SuccessTitle>
        </Column>
      </Row>
      <Row>
        <S.Column size={{ xs: 12, sm: 6 }} hasSeparator={true}>
          <S.Span>{capfl(t('visit us ;)'))}</S.Span>
        </S.Column>
        <S.Column size={{ xs: 12, sm: 6 }}>
          {t('more than 40x in slovakia')}
          <br />
          {t('from bratislava to humenne')}
        </S.Column>
      </Row>
      <Row>
        <S.Column size={{ xs: 12, sm: 6 }}>
          <S.StoreImage src={store1} alt="" staticImage={true} />
        </S.Column>
        <S.Column size={{ xs: 12, sm: 6 }}>
          <S.StoreImage src={store2} alt="" staticImage={true} />
        </S.Column>
      </Row>
    </S.SuccessWrapper>
  );
};

S.Wrapper = styled.div`
  position: relative;
`;

S.Header = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

S.HeaderItem = styled.span`
  color: ${({ theme }) => _.get(theme, 'colors.primary', 'black')};
  font-size: 1em;
`;

S.Image = styled(Image)`
  margin-right: 10px;
  vertical-align: middle;
`;

S.Price = styled(Price)`
  display: inline-block;
  margin-left: auto;
  // line-height: 65px;
  font-size: 18px;
  span {
    color: ${({ theme }) => _.get(theme, 'colors.primary', 'black')};
  }
`;

S.ButtonWrapper = styled.div`
  display: flex;
  margin: 40px 0 10px 0;
  align-items: center;
  justify-content: center;
  padding: 10px 0;
`;

S.ButtonReserve = styled(Button)`
  min-width: 160px;
  text-transform: uppercase;
`;

S.ErrorBar = styled.section`
  padding: 10px;
  text-align: center;
  font-weight: 600;
  font-size: 0.86em;
  color: ${({ theme }) => _.get(theme, 'colors.danger', 'black')};
`;

S.Product = styled.div`
  display: flex;
  align-items: center;
  padding: 5px 0;
  border: 2px solid;
  border-left: 0;
  border-right: 0;
  border-bottom: 0;
  border-image: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(226, 226, 226, 1) 48%,
    rgba(255, 255, 255, 0) 100%
  );
  border-image-slice: 1;
  font-size: 14px;
  font-weight: bold;
  color: ${({ theme }) => _.get(theme, 'colors.primary', 'red')} !important;
  margin-top: 5px;
`;

S.Row = styled(Row)`
  border: 2px solid;
  border-left: 0;
  border-right: 0;
  border-bottom: 0;
  border-image: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(226, 226, 226, 1) 48%,
    rgba(255, 255, 255, 0) 100%
  );
  border-image-slice: 1;
  padding-top: 30px;
`;

S.InputGroup = styled(InputGroup)`
  font-size: 14px;
  margin-bottom: 15px;
`;

S.Notice = styled.p`
  color: ${({ theme }) => _.get(theme, 'modal.notice.color', 'black')};
  margin-top: 30px;
  text-align: center;
  font-size: 0.82em;
  line-height: 1.5em;
`;

S.Link = styled(Link)`
  color: ${({ theme }) => _.get(theme, 'modal.notice.color', 'black')};
`;

S.Overlay = styled(SS.Overlay)`
  border-radius: 10px;
`;

class ReservationForm extends PureComponent {
  render() {
    const {
      data,
      values,
      errors,
      touched,
      handleChange,
      handleBlur,
      handleSubmit,
      setFieldValue,
      setFieldTouched,
      isSubmitting,
      isLoading = false,
      tacLink,
      t
    } = this.props;
    const { reservation = {} } = data;
    const { success = false } = reservation;
    const { product } = data.product;
    if (!_.isEmpty(product)) {
      const title = success ? '' : capfl(t('product reservation'));
      return (
        <Modal modalId="product_reservation" title={title}>
          {!_.get(reservation, 'success', false) ? (
            <form onSubmit={handleSubmit}>
              <S.Wrapper>
                <Container>
                  <Row>
                    <S.Header size={{ xs: 12 }}>
                      <S.HeaderItem>{capfl(t('product name'))}</S.HeaderItem>
                      <S.HeaderItem>{capfl(t('price in-store'))}</S.HeaderItem>
                    </S.Header>
                  </Row>
                  <Row>
                    <Column size={{ xs: 12 }}>
                      <S.Product>
                        <S.Image {...product.photo} size="50x50.jpg" />
                        {product.name}
                        <S.Price
                          price={_.get(product, 'retailPriceData.priceFormatted', _.get(product, 'finalPriceData.priceFormatted'))}
                          finalPrice={_.get(product, 'retailPriceData.priceFormatted', _.get(product, 'finalPriceData.priceFormatted'))}
                          finalPriceWithoutDPH={_.get(product, 'retailPriceData.priceWithoutVatFormatted', _.get(product, 'finalPriceData.priceWithoutVatFormatted'))}
                          showDiscount={false}
                          showWithoutVat={true}
                        />
                      </S.Product>
                    </Column>
                  </Row>
                  <S.Row>
                    <Column size={{ xs: 12, md: 6 }}>
                      <S.InputGroup
                        inputProps={{
                          id: 'name',
                          name: 'name',
                          onChange: handleChange,
                          onBlur: handleBlur,
                          value: values.name ? values.name : '',
                          placeholder: capfl(t('name'))
                        }}
                        error={touched.name && errors.name ? errors.name : ''}
                      />
                    </Column>

                    <Column size={{ xs: 12, md: 6 }}>
                      <S.InputGroup
                        inputProps={{
                          id: 'surname',
                          name: 'surname',
                          onChange: handleChange,
                          onBlur: handleBlur,
                          value: values.surname ? values.surname : '',
                          placeholder: capfl(t('surname'))
                        }}
                        error={
                          touched.surname && errors.surname
                            ? errors.surname
                            : ''
                        }
                      />
                    </Column>
                  </S.Row>

                  <Row>
                    <Column size={{ xs: 12, md: 6 }}>
                      <S.InputGroup
                        inputProps={{
                          id: 'phone',
                          name: 'phone',
                          onChange: handleChange,
                          onBlur: handleBlur,
                          value: values.phone ? values.phone : '',
                          placeholder: capfl(t('phone number'))
                        }}
                        error={
                          touched.phone && errors.phone ? errors.phone : ''
                        }
                      />
                    </Column>

                    <Column size={{ xs: 12, md: 6 }}>
                      <S.InputGroup
                        inputProps={{
                          id: 'email',
                          name: 'email',
                          type: 'email',
                          onChange: handleChange,
                          onBlur: handleBlur,
                          value: values.email ? values.email : '',
                          placeholder: capfl(t('email'))
                        }}
                        error={
                          touched.email && errors.email ? errors.email : ''
                        }
                      />
                    </Column>
                  </Row>

                  <S.Row>
                    <Column size={{ xs: 12, md: 12 }}>
                      <SelectGroup
                        selectProps={{
                          id: 'deliveryType',
                          name: 'deliveryType',
                          defaultValue: values.deliveryType
                            ? values.deliveryType
                            : null,
                          options: _.map(
                            _.sortBy(reservation.deliveries, groupItem => {
                              return _.deburr(groupItem.name);
                            }),
                            option => {
                              return { label: option.name, value: option.id };
                            }
                          ),
                          prompt: {
                            label: t('select store'),
                            value: ''
                          },
                          size: 'small',
                          setFieldValue,
                          setFieldTouched,
                          placeholder: capfl(t('select store'))
                        }}
                        error={
                          touched.deliveryType && errors.deliveryType
                            ? errors.deliveryType
                            : ''
                        }
                      />

                      <InputGroup
                        inputProps={{
                          id: 'productId',
                          name: 'productId',
                          type: 'hidden',
                          value: values.product ? values.product.id : null,
                          onChange: handleChange
                        }}
                      />

                      <InputGroup
                        inputProps={{
                          id: 'sizeId',
                          name: 'sizeId',
                          type: 'hidden',
                          value: values.size ? values.size.value : null,
                          onChange: handleChange
                        }}
                      />

                      <InputGroup
                        inputProps={{
                          id: 'count',
                          name: 'count',
                          type: 'hidden',
                          value: values.count ? values.count : null,
                          onChange: handleChange
                        }}
                      />
                    </Column>
                  </S.Row>
                </Container>

                {errors[ERROR_NAME_NOTHING_PICKED] ? (
                  <S.ErrorBar>{errors[ERROR_NAME_NOTHING_PICKED]}</S.ErrorBar>
                ) : (
                  ''
                )}
                <S.ButtonWrapper>
                  <S.ButtonReserve type="submit">
                    {isSubmitting ? (
                      <Icon className="nc-circle-02-1" />
                    ) : (
                      capfl(t('store reservation'))
                    )}
                  </S.ButtonReserve>
                </S.ButtonWrapper>
                <S.Notice>
                  {capfl(
                    t(
                      'products purchased via reservation cannot be returned within 14 days'
                    )
                  )}{' '}
                  (
                  <S.Link href={tacLink} target="_blank">
                    {capfl(t('terms and conditions'))}
                  </S.Link>
                  )
                </S.Notice>
              </S.Wrapper>
            </form>
          ) : (
            <SuccessfullySent t={t} />
          )}
          {isLoading ? (
            <S.Overlay>
              <img src={loader} alt="loader" />
            </S.Overlay>
          ) : (
            ''
          )}
        </Modal>
      );
    }
    return null;
  }
}

const fromInitalValues = {
  name: '',
  surname: '',
  phone: '',
  email: '',
  deliveryType: ''
};

const getFormMessages = t => {
  return {
    name: {
      required: capfl(`${t('name')} ${t('is required')}!`)
    },
    surname: {
      required: capfl(`${t('surname')} ${t('is required')}!`)
    },
    phone: {
      required: capfl(`${t('phone number')} ${t('is required')}!`)
    },
    email: {
      required: capfl(`${t('email')} ${t('is required')}!`),
      invalid: capfl(t('invalid email address'))
    },
    deliveryType: {
      required: capfl(`${t('store location')} ${t('is required')}!`)
    }
  };
};

const formikEnhancer = withFormik({
  enableReinitialize: true,
  mapPropsToValues: props => {
    let propsToReturn = { ...fromInitalValues };
    const { data, user } = props;
    const { product, reservation } = data;
    const { size, count } = reservation;
    const { store } = product.product;

    _.set(propsToReturn, 'product', _.get(product, 'product', {}));
    _.set(
      propsToReturn,
      'size',
      size
        ? size
        : _.isArray(store)
        ? { value: store[0].id, label: store[0].name }
        : null
    );
    _.set(propsToReturn, 'count', count || 1);

    if (user) {
      propsToReturn.email = _.get(user, 'email', null);
      propsToReturn.name = _.get(user, 'name', null);
      propsToReturn.surname = _.get(user, 'surname', null);
      propsToReturn.phone = _.get(user, 'phone', null);
    }
    return propsToReturn;
  },
  validationSchema: ({ t }) => {
    const messages = getFormMessages(t);
    return yup.object().shape({
      email: yup
        .string()
        .email(messages.email.invalid)
        .required(messages.email.required),
      name: yup.string().required(messages.name.required),
      surname: yup.string().required(messages.surname.required),
      phone: yup
        .string()
        .nullable()
        .required(messages.phone.required),
      deliveryType: yup.string().required(messages.deliveryType.required),
      productId: yup.string(),
      sizeId: yup.string(),
      count: yup.number()
    });
  },
  handleSubmit: (values, { props, setSubmitting }) => {
    const { product, submitReservation } = props;
    if (!_.isEmpty(values)) {
      setSubmitting(true);
      submitReservation(values, product);
      setSubmitting(false);
      const finalPrice = _.get(values, 'product.finalPriceData.price', 0);
      const count = _.get(values, 'count', 1);
      fireGTMEvent(PRODUCT_RESERVATION, [{ value: finalPrice * count }]);
    }
  },
  displayName: 'ReservationForm'
});

const EnhancedReservationForm = formikEnhancer(ReservationForm);

const makeMapStateToProps = () => {
  const getSettings = getWebInstanceSettingsSelector();
  const getPage = getPageSelector();
  const getUser = getUserSelector();
  const getPageIsLoading = getPageIsLoadingSelector();
  const mapStateToProps = (state, props) => {
    const user = getUser(state, props);
    const settings = getSettings(state, props);
    const isLoading = getPageIsLoading(state, props);
    return {
      isLoading,
      data: getPage(state, props),
      user: _.get(user, 'attributes', user),
      tacLink: _.get(settings, 'terms_and_conditions_link', '#')
    };
  };
  return mapStateToProps;
};

const mapDispatchToProps = dispatch => {
  return {
    submitReservation: (values, showLoading = false) => {
      dispatch(createReservation(values, showLoading));
    }
  };
};

const ReservationFormConnected = connect(
  makeMapStateToProps,
  mapDispatchToProps
)(EnhancedReservationForm);

export default withTranslation()(ReservationFormConnected);
